import { useEffect, useState } from "react";

import {
  ProjectUtilizationReport,
  type ProjectUtilizationReportProps,
} from "../../components/features/project-reports/components/project-utilization-report/project-utilization-report.tsx";
import { ProjectReportsToolbar } from "../../components/features/project-reports/components/project-reports-toolbar/project-reports-toolbar.tsx";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useProjectOptions } from "../../hooks/project-reports/use-project-options.ts";
import { useProjectUtilizationReport } from "../../hooks/project-reports/use-project-utilization-report.ts";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import type { PageProps } from "../../types/page.ts";
import type { Period, PeriodRange } from "../../components/features/time-allocated-overview/types.ts";
import { defaultRangeForPeriod, periodRangeToApiDates } from "../../utils/period-range.ts";

import styles from "./project-reports-page.module.css";

const PAGE_TITLE = "Project Reports";

function formatSelectedDuration(range: PeriodRange) {
  const dates = periodRangeToApiDates(range);
  if (!dates) return "";

  const format = (value: string) =>
    new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return `${format(dates.from)} - ${format(dates.to)}`;
}

const ProjectReportsPage = ({ title, pageMeta }: PageProps) => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const pageTitle = pageMeta?.title?.trim() || title?.trim() || PAGE_TITLE;

  const [projectId, setProjectId] = useState("");
  const [period, setPeriod] = useState<Period>("MONTHLY");
  const [range, setRange] = useState<PeriodRange>(() =>
    defaultRangeForPeriod("MONTHLY"),
  );

  const {
    options: projectOptions,
    loading: projectsLoading,
    error: projectsError,
  } = useProjectOptions();
  const {
    data: reportData,
    loading: reportLoading,
    error: reportError,
  } = useProjectUtilizationReport(projectId, range);

  const hasCompleteRange = Boolean(periodRangeToApiDates(range));
  const selectedDurationLabel = formatSelectedDuration(range);
  const reportStatus: ProjectUtilizationReportProps["status"] = !projectId
    ? "idle"
    : !hasCompleteRange
      ? "incomplete-range"
      : reportLoading
        ? "loading"
        : reportError
          ? "error"
          : "ready";

  const handlePeriodChange = (nextPeriod: Period) => {
    setPeriod(nextPeriod);
    setRange(defaultRangeForPeriod(nextPeriod));
  };

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs, pageTitle]);

  return (
    <PageShell className={styles.shell}>
      <ProjectReportsToolbar
        projectId={projectId}
        onProjectIdChange={setProjectId}
        projectOptions={projectOptions}
        projectsLoading={projectsLoading}
        projectsError={projectsError}
        period={period}
        onPeriodChange={handlePeriodChange}
        range={range}
        onRangeChange={setRange}
      />
      <ProjectUtilizationReport
        status={reportStatus}
        error={reportError}
        data={reportData}
        selectedDurationLabel={selectedDurationLabel}
      />
    </PageShell>
  );
};

export default ProjectReportsPage;
